import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar, Clock, MapPin, Stethoscope, UserRound, Home,
  Heart, MessageCircle, RefreshCw, CheckCircle, X, ChevronRight,
} from 'lucide-react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentService, Appointment } from '@lib/supabase/appointment.service'
import { providerService, Provider } from '@lib/supabase/provider.service'
import { clsx } from 'clsx'
import ConfirmationModal from '@components/web/ConfirmationModal'
import StatusModal from '../../components/web/StatusModal'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { queryKeys } from '@lib/queryKeys'
import { analyticsService } from '@lib/analytics/posthog.service'

const LOAD_STEP = 4
const FETCH_BATCH_SIZE = 8

// ---- helpers ----------------------------------------------------------------
const DERMATOLOGY_ICON = '/dermatology.png'

const SERVICE_META: Record<string, { textColor: string; bg: string; border: string; icon: any }> = {
  'Doctor Visit':      { textColor: 'text-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-400',    icon: Stethoscope },
  'Nurse Care':        { textColor: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-400', icon: UserRound },
  'Home Care':         { textColor: 'text-purple-600',  bg: 'bg-purple-50',  border: 'border-purple-400',  icon: Home },
  'Specialized Care':  { textColor: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-400',    icon: Heart },
  Dermatology:        { textColor: 'text-teal-600',    bg: 'bg-teal-50',    border: 'border-teal-400',    icon: 'dermatology' },
  'Chat Consultation': { textColor: 'text-sky-600',     bg: 'bg-sky-50',     border: 'border-sky-400',     icon: MessageCircle },
}

const STATUS_STYLE: Record<string, string> = {
  scheduled: 'bg-green-100 text-green-700',
  requested: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-indigo-100 text-indigo-700',
  cancelled: 'bg-red-100 text-red-700',
  rejected:  'bg-red-100 text-red-700',
}

function normalizeStatus(status?: string | null) {
  return (status || '').toLowerCase().trim()
}

function formatTime12h(t?: string | null) {
  if (!t || t === '—') return ''
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

type AptWithProvider = Appointment & { _provider: Provider | null }

const getAppointmentMergeKey = (appointment: Appointment) =>
  appointment.service_type === 'Chat Consultation'
    ? appointment.conversation_id || appointment.id
    : appointment.id

const mergeAppointments = (current: AptWithProvider[], next: AptWithProvider[]) => {
  const merged = [...current]
  const seen = new Set(current.map((appointment) => getAppointmentMergeKey(appointment)))

  for (const appointment of next) {
    const key = getAppointmentMergeKey(appointment)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(appointment)
  }

  return merged
}

// ---- Appointment Card -------------------------------------------------------
function AppointmentCard({
  apt,
  onViewDetails,
  onCancel,
  onMarkCompleted,
  isUpdating,
}: {
  apt: AptWithProvider
  onViewDetails: () => void
  onCancel: () => void
  onMarkCompleted: () => void
  isUpdating: boolean
}) {
  const meta = SERVICE_META[apt.service_type] ?? SERVICE_META['Doctor Visit']
  const Icon = meta.icon === 'dermatology' ? null : meta.icon
  const isChat = apt.service_type === 'Chat Consultation'
  const normalizedStatus = normalizeStatus(apt.status)

  return (
    <div className={clsx('bg-card border border-l-4 border-border overflow-hidden', meta.border)}>
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start gap-3">
          <div className={clsx('w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden', meta.bg)}>
            {apt._provider?.profile_image_url ? (
              <img src={apt._provider.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : apt.service_type === 'Dermatology' ? (
              <img src={DERMATOLOGY_ICON} alt="" aria-hidden="true" className="h-5 w-5 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
            ) : Icon ? (
              <Icon size={20} className={meta.textColor} />
            ) : null}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-semibold text-foreground truncate">
                {apt._provider?.name ?? 'Unknown Provider'}
              </p>
              <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium capitalize', STATUS_STYLE[normalizedStatus] ?? 'bg-muted text-muted-foreground')}>
                {apt.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{apt._provider?.specialty ?? '—'}</p>
            <span className={clsx('inline-block mt-1 text-xs px-2 py-0.5 rounded-md font-medium', meta.bg, meta.textColor)}>
              {apt.service_type}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar size={12} />
            {new Date(apt.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {apt.scheduled_time && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock size={12} />
              {formatTime12h(apt.scheduled_time)}
            </div>
          )}
          {apt.location && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin size={12} />
              <span className="truncate">{apt.location}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button
            onClick={onViewDetails}
            className="flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
          >
            {isChat ? 'Open Chat' : 'View Details'} <ChevronRight size={14} />
          </button>

          {normalizedStatus === 'scheduled' && (
            <button
              disabled={isUpdating}
              onClick={onMarkCompleted}
              className={clsx(
                'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors',
                isUpdating && 'opacity-50 cursor-not-allowed'
              )}
            >
              <CheckCircle size={13} /> Mark Completed
            </button>
          )}

          {normalizedStatus === 'requested' && (
            <button
              disabled={isUpdating}
              onClick={onCancel}
              className={clsx(
                'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors',
                isUpdating && 'opacity-50 cursor-not-allowed'
              )}
            >
              <X size={13} /> Cancel Request
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---- main -------------------------------------------------------------------
const FILTERS = ['All', 'Scheduled', 'Requested', 'Completed', 'Cancelled'] as const
type Filter = typeof FILTERS[number]

export default function Appointments() {
  const navigate = useNavigate()
  const { patient } = usePatientAuth()
  const [filter, setFilter] = useState<Filter>('All')
  const queryClient = useQueryClient()
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(LOAD_STEP)
  const [pendingAction, setPendingAction] = useState<{ type: 'cancel' | 'complete'; appointment: AptWithProvider } | null>(null)
  const [statusModal, setStatusModal] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
  } | null>(null)

  const statusParam = filter === 'All' ? undefined : filter.toLowerCase()
  const appointmentsQuery = useInfiniteQuery({
    queryKey: queryKeys.patient.appointmentPages(patient?.id, statusParam),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await appointmentService.getPatientAppointmentsPage(statusParam, FETCH_BATCH_SIZE, pageParam, undefined, 'Chat Consultation')
      const providerMap = await providerService.getProvidersByIds(
        result.appointments.map((appointment) => appointment.provider_id)
      )

      return {
        ...result,
        appointments: result.appointments.map((appointment) => ({
          ...appointment,
          _provider: providerMap[appointment.provider_id] ?? null,
        })),
      }
    },
    enabled: !!patient?.id,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  })

  const appointments = useMemo(() => {
    const pages = appointmentsQuery.data?.pages ?? []
    return pages.reduce<AptWithProvider[]>((current, page) => mergeAppointments(current, page.appointments), [])
  }, [appointmentsQuery.data])

  const isLoading = appointmentsQuery.isPending
  const isRefreshing = appointmentsQuery.isRefetching && !appointmentsQuery.isFetchingNextPage
  const isLoadingMore = appointmentsQuery.isFetchingNextPage
  const error = appointmentsQuery.isError ? 'Failed to load appointments. Please try again.' : null
  const hasMore = Boolean(appointmentsQuery.hasNextPage)

  const handleRefresh = async () => {
    await appointmentsQuery.refetch()
  }

  const handleLoadMore = async () => {
    if (visibleCount < appointments.length) {
      setVisibleCount((count) => count + LOAD_STEP)
      return
    }

    if (!hasMore || isLoadingMore) return

    const result = await appointmentsQuery.fetchNextPage()
    if (!result.isError) {
      setVisibleCount((count) => count + LOAD_STEP)
    }
  }

  const handleCancel = async (apt: AptWithProvider) => {
    analyticsService.capture('cancellation_submitted', {
      source: 'patient_appointments_screen',
      status: apt.status,
      service_type: apt.service_type,
      platform: 'web',
    })
    setUpdatingId(apt.id)
    const result = await appointmentService.cancelAppointmentWithRefund(apt.id)
    setUpdatingId(null)
    setPendingAction(null)
    if (result.success) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointmentPages(patient?.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointments(patient?.id) })
      setStatusModal({
        type: 'success',
        title: 'Appointment Cancelled',
        message: (result.refundAmount ?? 0) > 0
          ? `₦${result.refundAmount!.toLocaleString()} has been refunded to your wallet.`
          : 'Your appointment has been cancelled successfully.',
      })
    } else {
      analyticsService.capture('cancellation_failed', {
        source: 'patient_appointments_screen',
        error_type: 'service_returned_failure',
        service_type: apt.service_type,
        platform: 'web',
      })
      setStatusModal({
        type: 'error',
        title: 'Cancellation Failed',
        message: result.error ?? 'Failed to cancel appointment.',
      })
    }
  }

  const handleMarkCompleted = async (apt: AptWithProvider) => {
    setUpdatingId(apt.id)
    const result = await appointmentService.updateAppointmentStatus(apt.id, 'completed')
    setUpdatingId(null)
    setPendingAction(null)
    if (result.success) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointmentPages(patient?.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointments(patient?.id) })
    } else {
      alert(result.error ?? 'Failed to update appointment.')
    }
  }

  const handleRetry = () => {
    void appointmentsQuery.refetch()
  }

  const displayedAppointments = appointments.slice(0, visibleCount)
  const canLoadMore = visibleCount < appointments.length || hasMore
  const pendingActionConfig = pendingAction
    ? pendingAction.type === 'cancel'
      ? {
          title: 'Cancel appointment?',
          message: `Cancel your appointment with ${pendingAction.appointment._provider?.name ?? 'this provider'}? Any eligible refund will be applied to your wallet.`,
          confirmLabel: 'Cancel Appointment',
          confirmVariant: 'destructive' as const,
          onConfirm: () => handleCancel(pendingAction.appointment),
        }
      : {
          title: 'Mark appointment as completed?',
          message: `Confirm that your appointment with ${pendingAction.appointment._provider?.name ?? 'this provider'} has been completed.`,
          confirmLabel: 'Mark Completed',
          confirmVariant: 'primary' as const,
          onConfirm: () => handleMarkCompleted(pendingAction.appointment),
        }
    : null

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Appointments</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Showing {displayedAppointments.length} {filter === 'All' ? 'appointments' : filter.toLowerCase()}
          </p>
        </div>
        <button
          title="Refresh appointments"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <RefreshCw size={18} className={clsx('text-muted-foreground', isRefreshing && 'animate-spin')} />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => {
              setFilter(f)
              setVisibleCount(LOAD_STEP)
            }}
            className={clsx(
              'shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all',
              filter === f
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-foreground hover:bg-muted'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-36 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{error}</p>
          <button onClick={handleRetry} className="mt-3 text-sm text-primary hover:underline">
            Retry
          </button>
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-16 bg-card border border-dashed border-border rounded-xl">
          <Calendar size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-foreground">No {filter === 'All' ? '' : filter.toLowerCase()} appointments</p>
          <p className="text-xs text-muted-foreground mt-1">
            {filter === 'All' ? 'Book a service to get started' : `You have no ${filter.toLowerCase()} appointments`}
          </p>
          {filter === 'All' && (
            <button
              onClick={() => navigate('/patient/dashboard')}
              className="mt-4 text-sm text-primary hover:underline"
            >
              Find Care
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {displayedAppointments.map((apt) => (
            <AppointmentCard
              key={apt.id}
              apt={apt}
              isUpdating={updatingId === apt.id}
              onViewDetails={() => navigate(
                apt.service_type === 'Chat Consultation' ? `/patient/chat/${apt.id}` : `/patient/appointments/${apt.id}`,
                apt.service_type === 'Chat Consultation' ? { state: { backPath: '/patient/appointments' } } : undefined
              )}
              onCancel={() => setPendingAction({ type: 'cancel', appointment: apt })}
              onMarkCompleted={() => setPendingAction({ type: 'complete', appointment: apt })}
            />
          ))}

          {canLoadMore && (
            <div className="pt-2">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="w-full sm:w-auto px-4 py-2 border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                {isLoadingMore ? 'Loading...' : 'Load 4 More'}
              </button>
            </div>
          )}
        </div>
      )}

      {pendingActionConfig && (
        <ConfirmationModal
          title={pendingActionConfig.title}
          message={pendingActionConfig.message}
          confirmLabel={pendingActionConfig.confirmLabel}
          confirmVariant={pendingActionConfig.confirmVariant}
          isLoading={updatingId === pendingAction?.appointment.id}
          onCancel={() => setPendingAction(null)}
          onConfirm={pendingActionConfig.onConfirm}
        />
      )}

      {statusModal && (
        <StatusModal
          type={statusModal.type}
          title={statusModal.title}
          message={statusModal.message}
          onPrimary={() => setStatusModal(null)}
        />
      )}
    </div>
  )
}
